/**
 * Post Summary - 관련 글 카드용 경량 캐시
 *
 * CMS 원본이 없는 파생 캐시다. 포스트의 요약(title·image·category·createdAt)만 포스트별로
 * 저장한다(`getPostSummary-{slug}`). writer 는 두 갈래다 — 블로그 포스트 저장 훅
 * (wordpress-webhook.ts 의 updateSinglePostCache/deleteSinglePostCache)과 init 스크립트의
 * 전체 재생성(initAllPostSummaryCache)은 이미 손에 쥔 아이템으로 `writePostSummary` 를
 * 직접 불러 쓴다(재조회 없음). init 스크립트의 단건 재생성만 그 글의 상세 캐시 1건을
 * 조회해야 해서 `getPostSummary(slug, { refresh: true })` 를 쓴다.
 *
 * 왜 별도 캐시인가 — 관련 글 캐시는 slug 만 들고 있고 카드 표시 데이터는 그 글의 캐시에서
 * 읽는다. 상세 캐시(`getBlogNewsPost-blog-{slug}`)를 읽으면 카드 3장 그리자고 본문 3개분을
 * 전송하게 되므로, 본문 없는 요약만 따로 둔다. 블로그 상세 페이지 로더(load-detail-data.ts
 * 웨이브 3)가 관련글 카드용으로 이 캐시를 읽으면 그 행위가 `getPostSummary-{slug}` 태그를
 * 페이지에 바인딩하게 되어, 그 글이 저장·삭제될 때 그 글을 관련 글로 담은 페이지들만
 * 정확히 stale 된다.
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import {
  toPostSummary,
  type PostSummary,
  type PostSummarySource,
} from '@/utils/post-summary';
import { fetchFromCache, type FetchAPIOptions } from './api';
import { getBlogNewsPostBySlug } from './blog-news';
import {
  deleteWordpressCacheFromS3,
  uploadWordpressCacheDataToS3,
} from './wordpress-cache';

// ============================================
// 상수
// ============================================

/** 캐시 폴더명이자 revalidateTag 태그 — content-revalidation.ts 의 blog 설정과 일치해야 함 */
export const getPostSummaryCacheLabel = (slug: string) =>
  `getPostSummary-${slug}`;

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/** `fetchFromCache` 가 any 를 반환하므로 소비처의 필드 역참조 전에 형태를 확인한다 */
export const isPostSummary = (value: unknown): value is PostSummary => {
  if (!value || typeof value !== 'object') return false;

  const data = value as Partial<PostSummary>;
  return (
    typeof data.id === 'string' &&
    typeof data.slug === 'string' &&
    typeof data.title === 'string' &&
    typeof data.category === 'string' &&
    typeof data.image === 'string' &&
    typeof data.createdAt === 'string'
  );
};

/**
 * 완성된 요약을 `slug` 키로 S3 에 쓴다 — 업로드 로직의 유일한 자리.
 *
 * 페이로드의 `slug` 필드도 캐시 키와 같은 `slug` 로 덮어써 항상 일치시킨다 — 호출부가
 * 넘긴 요약 소스의 자체 slug(예: WordPress 응답의 빈 slug)를 신뢰하지 않는다. 상세
 * 캐시 키(`actualSlug`)와 요약 캐시 키가 어긋나면 무효화 태그가 엉뚱한 캐시를 가리키게
 * 된다.
 */
const uploadPostSummary = async (
  slug: string,
  summary: PostSummary,
): Promise<PostSummary> => {
  const keyed: PostSummary = { ...summary, slug };
  await uploadWordpressCacheDataToS3(keyed, getPostSummaryCacheLabel(slug));
  return keyed;
};

/**
 * 이미 정규화된 포스트(저장 훅이 손에 든 데이터, 또는 상세 캐시 항목)로 요약을 만들어
 * S3 에 쓴다. **S3 를 다시 읽지 않는다** — 저장 훅이 상세 캐시 write 직후 같은 데이터로
 * 바로 요약을 쓸 수 있어야 `invalidateIndividualCache` 의 태그 무효화보다 요약 write 가
 * 먼저 일어난다(순서 결함 수정의 핵심).
 *
 * 호출부(웹훅·init 스크립트의 전체 재생성)가 넘기는 `post` 는 상세 페이지 응답 전체
 * (`BlogNewsProp`, content·seoOpengraphImage 포함)이거나 블로그 목록 아이템
 * (`BlogNewsListItemProp`)일 수 있다 — `PostSummarySource` 는 타입 단언일 뿐이라 앞쪽
 * 형태의 필드들이 실제로 남아 있으면 `toPostSummary`(→ `resolvePostListImage`)가 더 깊은
 * 폴백까지 타 버린다. 그래서 여기서 명시적으로 9개 필드만 뽑아 새 객체를 만든 뒤
 * 변환한다 — 카드 이미지는 두 호출부 모두 항상 `heroImage → featuredImage` 까지만
 * 적용돼야 결과가 같다.
 *
 * 캐시 키는 `slug` 인자(호출부가 아는 정본 slug, 예: `actualSlug`)로 고정한다 — 이
 * 인자의 `post.slug` 필드가 아니다(운영 미반영 이유로 정본 slug가 소스 필드와 어긋날
 * 수 있음).
 */
export const writePostSummary = async (
  slug: string,
  post: PostSummarySource,
): Promise<PostSummary> => {
  const picked: PostSummarySource = {
    id: post.id,
    slug: post.slug,
    title: post.title,
    lang: post.lang,
    categoryKo: post.categoryKo,
    categoryEn: post.categoryEn,
    date: post.date,
    heroImage: post.heroImage,
    featuredImage: post.featuredImage,
  };

  return uploadPostSummary(slug, toPostSummary(picked));
};

/**
 * 요약 조회 (refresh 시 그 글의 상세 캐시가 없으면 null, 캐시 조회 실패도 null).
 *
 * 캐시에서 파생하므로 WordPress 재조회가 없다 — 그 글의 상세 캐시 1건만 조회해 요약을
 * 만든다. init 스크립트의 단건 재생성(initSinglePostSummaryCache) 등 운영자가 결과를
 * 직접 확인하는 경로가 이 refresh 경로를 쓴다. 저장 훅과 init 스크립트의 전체 재생성은
 * 이미 손에 쥔 아이템이 있어 이 함수를 거치지 않고 `writePostSummary` 를 직접 호출한다.
 */
export const getPostSummary = async (
  slug: string,
  options?: Pick<FetchAPIOptions, 'refresh'>,
): Promise<PostSummary | null> => {
  if (options?.refresh) {
    // 목록 전체(fetchAllFromCache 순차 페이지네이션, 500건 기준 10회 왕복) 대신
    // 그 글의 상세 캐시 1건만 읽는다.
    const post = await getBlogNewsPostBySlug(slug, CONTENT_CATEGORY_ID.blog);
    if (!post) return null;

    return writePostSummary(slug, post);
  }

  const cached = await fetchFromCache(getPostSummaryCacheLabel(slug));
  return isPostSummary(cached) ? cached : null;
};

export const deletePostSummaryCache = (slug: string): Promise<boolean> =>
  deleteWordpressCacheFromS3(getPostSummaryCacheLabel(slug));
